import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Zap, Shield, Clock, HeartHandshake, Award, Wrench } from "lucide-react";

const reasons = [
  {
    icon: Zap,
    title: "Alles aus einer Hand",
    description: "Bauunternehmung, Galabau und Sachverständigenleistungen – koordiniert und effizient unter einem Dach.",
  },
  {
    icon: Shield,
    title: "Geprüfte Qualität",
    description: "Höchste Qualitätsstandards in jedem Bereich. Zertifizierte Prozesse und regelmäßige Fortbildungen.",
  },
  {
    icon: Clock,
    title: "Termintreue",
    description: "Wir halten, was wir versprechen. Pünktlichkeit und zuverlässige Zeitplanung sind unser Markenzeichen.",
  },
  {
    icon: HeartHandshake,
    title: "Persönliche Betreuung",
    description: "Ein fester Ansprechpartner für Ihr Projekt. Direkte Kommunikation auf Augenhöhe.",
  },
  {
    icon: Award,
    title: "25+ Jahre Erfahrung",
    description: "Über zwei Jahrzehnte Branchenerfahrung in Bau, Landschaftsbau und Sachverständigenwesen.",
  },
  {
    icon: Wrench,
    title: "Eigene Fachkräfte",
    description: "Keine Subunternehmer – unsere eigenen qualifizierten Teams setzen Ihr Projekt um.",
  },
];

export const WarumSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });

  return (
    <section className="relative section-padding bg-background overflow-hidden" ref={ref}>
      {/* Subtle background */}
      <div
        className="absolute inset-0 bg-cover bg-center opacity-[0.03]"
        style={{ backgroundImage: `url(/images/projekt-buero.jpg)` }}
      />

      <div className="container-wide relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
          className="text-center mb-16 max-w-2xl mx-auto"
        >
          <span className="text-xs font-heading uppercase tracking-widest text-muted-foreground mb-3 block">
            Warum Freitag-Gruppe
          </span>
          <h2 className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl text-foreground mb-4">
            Ihre Vorteile auf einen Blick.
          </h2>
          <p className="text-sm md:text-base text-muted-foreground font-body leading-relaxed">
            Sechs gute Gründe, warum Sie sich für die Freitag-Gruppe entscheiden sollten.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-border">
          {reasons.map((reason, index) => {
            const Icon = reason.icon;
            return (
              <motion.div
                key={reason.title}
                initial={{ opacity: 0, y: 30 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.4, delay: index * 0.08 }}
              >
                <Card className="border-0 bg-card h-full group hover:bg-secondary/50" style={{ transition: 'background-color 0.3s' }}>
                  <CardContent className="p-8 lg:p-10">
                    <div className="flex items-start gap-5">
                      <div className="p-3 gradient-blue-subtle border border-brand-blue/20 shrink-0 group-hover:glow-blue" style={{ transition: 'box-shadow 0.3s' }}>
                        <Icon className="w-5 h-5 text-brand-blue-light" />
                      </div>
                      <div>
                        <h3 className="font-heading font-semibold text-base text-foreground mb-2">
                          {reason.title}
                        </h3>
                        <p className="text-sm text-muted-foreground font-body leading-relaxed">
                          {reason.description}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
